import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable";

import { SHOP_NAME, SHOP_ADDRESS, SHOP_PHONE } from '../Variables';
import shopLogo from '../images/shop_logo.png';

Modal.setAppElement('#root');

// Date formatter: 08 Nov 2025
const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  return date.toISOString().split('T')[0];
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return dateFormatter.format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

const Invoicing = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [newInvoice, setNewInvoice] = useState({
    customerId: '',
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    items: [{ description: '', quantity: 1, unitPrice: 0 }],
    taxRate: 0,
    isRecurring: false,
    recurrenceInterval: 'None',
    recurrenceEndDate: '',
    userId: user?.uid || '',
    status: 'Draft'
  });
  const [editingInvoice, setEditingInvoice] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isFetchingInvoices, setIsFetchingInvoices] = useState(true);
  const [hasFetchedInvoices, setHasFetchedInvoices] = useState(false);

  const invoicesCollectionRef = collection(db, "invoices");
  const customersCollectionRef = collection(db, "customers");
  const templatesCollectionRef = collection(db, "invoiceTemplates");

  useEffect(() => {
    if (user && userData) {
      fetchCustomers();
      fetchTemplates();
      fetchInvoices();
    }
  }, [user, userData]);

  useEffect(() => {
    const filtered = invoices.filter((invoice) =>
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customers.find(c => c.id === invoice.customerId)?.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredInvoices(filtered);
  }, [searchTerm, invoices, customers]);

  const fetchCustomers = async () => {
    if (!user?.uid) return;
    try {
      const q = query(customersCollectionRef, where("active", "==", true));
      const data = await getDocs(q);
      setCustomers(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch customers");
      setShowModal(true);
    }
  };

  const fetchTemplates = async () => {
    if (!user?.uid) return;
    try {
      const q = query(templatesCollectionRef);
      const data = await getDocs(q);
      setTemplates(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch templates");
      setShowModal(true);
    }
  };

  const fetchInvoices = async () => {
    if (!user?.uid) return;
    setIsFetchingInvoices(true);
    try {
      const q = query(invoicesCollectionRef);
      const data = await getDocs(q);
      setInvoices(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
      setHasFetchedInvoices(true);
    } catch (error) {
      setError("Failed to fetch invoices");
      setShowModal(true);
    } finally {
      setIsFetchingInvoices(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewInvoice({ ...newInvoice, [name]: value });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...newInvoice.items];
    if (field === 'quantity') updatedItems[index][field] = parseInt(value) || 0;
    else if (field === 'unitPrice') updatedItems[index][field] = parseFloat(value) || 0;
    else updatedItems[index][field] = value;
    setNewInvoice({ ...newInvoice, items: updatedItems });
  };

  const addItem = () => {
    setNewInvoice({
      ...newInvoice,
      items: [...newInvoice.items, { description: '', quantity: 1, unitPrice: 0 }]
    });
  };

  const removeItem = (index) => {
    setNewInvoice({
      ...newInvoice,
      items: newInvoice.items.filter((_, i) => i !== index)
    });
  };

  const calculateSubtotal = () => newInvoice.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const calculateTax = () => calculateSubtotal() * (newInvoice.taxRate / 100);
  const calculateTotal = () => calculateSubtotal() + calculateTax();

  const handleAddInvoice = async () => {
    if (!newInvoice.customerId || !newInvoice.invoiceNumber.trim() || !newInvoice.dueDate || newInvoice.items.length === 0) {
      setError("Customer, invoice number, due date, and at least one item are required");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(invoicesCollectionRef, {
        ...newInvoice,
        userId: user?.uid,
        subtotal: calculateSubtotal(),
        tax: calculateTax(),
        total: calculateTotal(),
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Invoice added successfully");
      fetchInvoices();
      resetNewInvoice();
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add invoice");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateInvoice = async () => {
    setUpdate(true);
    try {
      const invoiceDoc = doc(db, "invoices", editingInvoice.id);
      await updateDoc(invoiceDoc, {
        ...newInvoice,
        subtotal: calculateSubtotal(),
        tax: calculateTax(),
        total: calculateTotal()
      });
      setSuccessMessage("Invoice updated successfully");
      fetchInvoices();
      resetNewInvoice();
      setEditingInvoice(null);
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update invoice");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleSaveTemplateDirect = async () => {
    if (!newInvoice.items.some(item => item.description.trim())) {
      setError("Add at least one item with description to save as template");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      const customer = customers.find(c => c.id === newInvoice.customerId);
      const name = customer ? `Template - ${customer.name}` : `General Template - ${formatDisplayDate(new Date())}`;

      await addDoc(templatesCollectionRef, {
        name,
        items: newInvoice.items.map(i => ({ ...i })),
        taxRate: newInvoice.taxRate,
        isRecurring: newInvoice.isRecurring,
        recurrenceInterval: newInvoice.recurrenceInterval,
        recurrenceEndDate: newInvoice.recurrenceEndDate,
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Template saved successfully!");
      await fetchTemplates();
    } catch (error) {
      setError("Failed to save template");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const applyTemplate = (template) => {
    setNewInvoice({
      ...newInvoice,
      items: template.items.map(i => ({ ...i })),
      taxRate: template.taxRate,
      isRecurring: template.isRecurring,
      recurrenceInterval: template.recurrenceInterval,
      recurrenceEndDate: template.recurrenceEndDate
    });
    setIsTemplateModalOpen(false);
    setIsAddModalOpen(true);
  };

  const resetNewInvoice = () => {
    setNewInvoice({
      customerId: '',
      invoiceNumber: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      items: [{ description: '', quantity: 1, unitPrice: 0 }],
      taxRate: 0,
      isRecurring: false,
      recurrenceInterval: 'None',
      recurrenceEndDate: '',
      userId: user?.uid || '',
      status: 'Draft'
    });
  };

  const handleEditInvoice = (invoiceToEdit) => {
    setEditingInvoice(invoiceToEdit);
    setNewInvoice({
      customerId: invoiceToEdit.customerId || '',
      invoiceNumber: invoiceToEdit.invoiceNumber || '',
      date: formatDateForInput(invoiceToEdit.date),
      dueDate: formatDateForInput(invoiceToEdit.dueDate),
      items: invoiceToEdit.items.map(item => ({
        description: item.description || '',
        quantity: Number(item.quantity) || 1,
        unitPrice: Number(item.unitPrice) || 0
      })),
      taxRate: Number(invoiceToEdit.taxRate) || 0,
      isRecurring: invoiceToEdit.isRecurring || false,
      recurrenceInterval: invoiceToEdit.recurrenceInterval || 'None',
      recurrenceEndDate: formatDateForInput(invoiceToEdit.recurrenceEndDate),
      userId: invoiceToEdit.userId,
      status: invoiceToEdit.status || 'Draft'
    });
    setIsEditModalOpen(true);
  };

  const generateInvoicePDF = (invoice, customer) => {
    // --- MODERN DESIGN CONSTANTS ---
    const PRIMARY_COLOR = '#0077B6'; // Deep Blue for modern, professional feel
    const LIGHT_BG = '#F0F5F9'; // Very light grey/blue for section backgrounds
    const TEXT_COLOR = '#333333';
    const GRAY_TEXT = '#777777';

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = margin; // Starting Y position

    // --- 1. HEADER SECTION: Logo, Title, and Address ---

    // Background rectangle for the top header area
    doc.setFillColor(LIGHT_BG);
    doc.rect(0, 0, pageWidth, 55, 'F'); // Fill entire top section

    try {
      if (shopLogo) doc.addImage(shopLogo, 'PNG', margin, y, 30, 30);
    } catch (err) { console.warn("Logo failed"); }

    // Shop Name (Primary Accent Color, Larger)
    doc.setFontSize(28).setFont(undefined, 'bold');
    doc.setTextColor(PRIMARY_COLOR);
    doc.text(SHOP_NAME, pageWidth - margin, y + 8, { align: 'right' });

    // Shop Address and Contact (Grey text)
    doc.setFontSize(10).setFont(undefined, 'normal');
    doc.setTextColor(TEXT_COLOR);
    const addr = doc.splitTextToSize(SHOP_ADDRESS, 80);
    doc.text(addr, pageWidth - margin, y + 15, { align: 'right' });
    doc.text(SHOP_PHONE, pageWidth - margin, y + 25, { align: 'right' });

    // Reset Y position and colors for main content
    y = 65;
    doc.setTextColor(TEXT_COLOR);

    // --- 2. INVOICE TITLE ---
    doc.setFontSize(24).setFont(undefined, 'bold');
    doc.text('INVOICE', margin, y);
    y += 15;

    // --- 3. CUSTOMER & INVOICE DETAILS SECTION (Organized) ---

    // Column 1: Invoice Details
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold').text('Invoice Details:', margin, y);
    doc.setFont(undefined, 'normal');
    doc.text(`Invoice #: ${invoice.invoiceNumber}`, margin, y + 6);
    doc.text(`Date: ${formatDisplayDate(invoice.date)}`, margin, y + 12);
    doc.text(`Due Date: ${formatDisplayDate(invoice.dueDate)}`, margin, y + 18);
    if (invoice.isRecurring) {
        doc.text(`Recurring: ${invoice.recurrenceInterval} until ${formatDisplayDate(invoice.recurrenceEndDate)}`, margin, y + 24);
    }

    // Column 2: Bill To
    const col2X = pageWidth / 2 + 10;
    doc.setFont(undefined, 'bold').text('Bill To:', col2X, y);
    doc.setFont(undefined, 'normal');
    doc.text(customer?.name || 'N/A', col2X, y + 6);
    doc.text(customer?.contactPhone || '', col2X, y + 12);
    doc.text(customer?.contactEmail || '', col2X, y + 18);

    y += 40; // Move y past the detail sections

    // --- 4. ITEMS TABLE ---

    const tableData = invoice.items.map(item => [
      item.description || '',
      item.quantity,
      formatAmount1(item.unitPrice, false),
      formatAmount1(item.quantity * item.unitPrice, false)
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Description', 'Qty', 'Unit Price', 'Amount']],
      body: tableData,
      theme: 'plain', // Use plain theme for modern look
      headStyles: {
          fillColor: PRIMARY_COLOR, // Primary color header
          textColor: 255, // White text
          fontStyle: 'bold',
          fontSize: 10
      },
      styles: {
          textColor: TEXT_COLOR,
          lineColor: '#DDDDDD',
          lineWidth: 0.1, // Subtle lines for separation
          fontSize: 9
      },
      columnStyles: {
          0: { cellWidth: 90 },
          1: { halign: 'center' }, // Center quantity
          2: { halign: 'right' },
          3: { halign: 'right', fontStyle: 'bold' } // Bold the final amount per line
      },
      margin: { left: margin, right: margin }
    });

    const finalY = doc.lastAutoTable.finalY + 10;

    // --- 5. SUMMARY / TOTALS ---

    doc.setFont(undefined, 'normal').setFontSize(10).setTextColor(TEXT_COLOR);
    const summaryX = pageWidth - margin - 50; // Align summary elements
    const valueX = pageWidth - margin;

    // Subtotal
    doc.text('Subtotal:', summaryX, finalY, { align: 'right' });
    doc.text(formatAmount1(invoice.subtotal), valueX, finalY, { align: 'right' });

    // Tax
    doc.text(`Tax (${invoice.taxRate}%):`, summaryX, finalY + 7, { align: 'right' });
    doc.text(formatAmount1(invoice.tax), valueX, finalY + 7, { align: 'right' });

    // Draw a separating line above total
    doc.setDrawColor(PRIMARY_COLOR);
    doc.line(pageWidth - 75, finalY + 12, pageWidth - margin, finalY + 12);

    // TOTAL (Highlighted)
    doc.setFont(undefined, 'bold').setFontSize(16);
    doc.setTextColor(PRIMARY_COLOR);
    doc.text('TOTAL DUE:', summaryX, finalY + 24, { align: 'right' });
    doc.text(formatAmount1(invoice.total), valueX, finalY + 24, { align: 'right' });


    // --- 6. FOOTER / TERMS ---
    doc.setTextColor(TEXT_COLOR);
    doc.setFontSize(9).setFont(undefined, 'normal');
    doc.text('Terms: Payment due by due date.', margin, finalY + 45);

    doc.setFontSize(8);
    doc.setTextColor(GRAY_TEXT);
    doc.text('Thank you for your business!', pageWidth / 2, doc.internal.pageSize.height - 15, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });

    return doc;
  };

  const handleDownloadPDF = (invoice) => {
    const customer = customers.find(c => c.id === invoice.customerId);
    if (!customer) {
      setError("Customer not found");
      setShowModal(true);
      return;
    }
    try {
      const doc = generateInvoicePDF(invoice, customer);
      doc.save(`Invoice_${invoice.invoiceNumber}_${formatDisplayDate(invoice.date)}.pdf`);
      setSuccessMessage("Invoice downloaded successfully");
      setShowModal(true);
    } catch (err) {
      setError("PDF generation failed");
      setShowModal(true);
    }
  };

  const formatAmount = (value, includeSymbol = true) => {
    const num = Number(value) || 0;
    const formatted = num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return includeSymbol ? `₦${formatted}` : formatted;
  };

  const formatAmount1 = (value) => `N${Number(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Cashier', 'Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingInvoices && !hasFetchedInvoices) return <p className="loading-text">Loading invoices...</p>;
    if (filteredInvoices.length === 0) {
      return <p className="loading-text">{searchTerm ? "No matches." : "No invoices. Add one."}</p>;
    }

    return filteredInvoices.map((invoiceItem) => (
      <div key={invoiceItem.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{invoiceItem.invoiceNumber}</strong><br />
            <span>{customers.find(c => c.id === invoiceItem.customerId)?.name || 'Unknown'}</span><br />
            <span>Total: {formatAmount(invoiceItem.total)}</span><br />
            <span>Due: {formatDisplayDate(invoiceItem.dueDate)}</span><br />
            <span className="bold size5 font-light-blue-cash">{invoiceItem.status}</span>
            {invoiceItem.isRecurring && <><br /><span>Recurring: {invoiceItem.recurrenceInterval}</span></>}
          </p>
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEditInvoice(invoiceItem)}>
            <i className="fas fa-edit"></i>
          </button>
          {invoiceItem.status === 'Draft' && (
            <button className="list-btn" onClick={() => handleDownloadPDF(invoiceItem)}>
              <i className="fas fa-download"></i>
            </button>
          )}
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Invoicing</h2>
            <Link to="#" className="home-link" onClick={() => fetchInvoices()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Invoice</button>
              <button className="login-button login-button-d caps" onClick={() => setIsTemplateModalOpen(true)}>Templates</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search invoice or customer"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">Access denied.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Modal */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => setIsAddModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Invoice</h2>
        <form className="session-form modal-scroll">
          <label className="input-label">Customer</label>
          <select name="customerId" className="input-field" value={newInvoice.customerId} onChange={handleInputChange} required>
            <option value="">Select</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <label className="input-label">Invoice Number</label>
          <input type="text" name="invoiceNumber" className="input-field" value={newInvoice.invoiceNumber} onChange={handleInputChange} required />

          <label className="input-label">Date</label>
          <input type="date" name="date" className="input-field" value={newInvoice.date} onChange={handleInputChange} />

          <label className="input-label">Due Date</label>
          <input type="date" name="dueDate" className="input-field" value={newInvoice.dueDate} onChange={handleInputChange} required />

          <label className="input-label">Tax Rate (%)</label>
          <input type="number" name="taxRate" className="input-field" value={newInvoice.taxRate} onChange={handleInputChange} step="0.01" />

          <label><input type="checkbox" checked={newInvoice.isRecurring} onChange={e => setNewInvoice({...newInvoice, isRecurring: e.target.checked})} /> Recurring</label>
          {newInvoice.isRecurring && (
            <>
              <select name="recurrenceInterval" className="input-field" value={newInvoice.recurrenceInterval} onChange={handleInputChange}>
                <option>Weekly</option>
                <option>Monthly</option>
                <option>Quarterly</option>
              </select>
              <input type="date" name="recurrenceEndDate" className="input-field" value={newInvoice.recurrenceEndDate} onChange={handleInputChange} />
            </>
          )}

          <h4>Items</h4>
          {newInvoice.items.map((item, i) => (
            <>
              <div key={i} className="column">
                <label className="input-label">Description</label>
                <input type="text" className="input-field" placeholder="Description" value={item.description} onChange={e => handleItemChange(i, 'description', e.target.value)} />
                <div className="row gap-1">
                  <div className="column">
                    <label className="input-label">Qunatity</label>
                    <input type="number" className="input-field" placeholder="Qty" value={item.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} />
                  </div>
                  <div className="column">
                    <label className="input-label">Unit Price</label>
                    <input type="number" className="input-field" placeholder="Price" value={item.unitPrice} onChange={e => handleItemChange(i, 'unitPrice', e.target.value)} step="0.01" />
                  </div>
                  <button type="button" className="remove-button" onClick={() => removeItem(i)}>
                    <i className="fas fa-trash font-red"></i>
                  </button>
                </div>                
              </div>
              <hr />
            </>
          ))}
          <button type="button" className="login-button" onClick={addItem}>Add Item</button>

          <p>Subtotal: {formatAmount(calculateSubtotal())}</p>
          <p>Tax: {formatAmount(calculateTax())}</p>
          <p>Total: {formatAmount(calculateTotal())}</p>

          <div className="row gap-1">
            <button type="button" className="login-button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button type="button" className="login-button" onClick={handleSaveTemplateDirect}>Template</button>
            <button type="button" className="login-button" onClick={handleAddInvoice}>Add</button>
          </div>
        </form>
      </Modal>

      {/* ========== FIXED EDIT MODAL - FULLY EDITABLE ========== */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => { setIsEditModalOpen(false); resetNewInvoice(); setEditingInvoice(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Invoice</h2>
        <form className="session-form modal-scroll">
          <label className="input-label">Customer</label>
          <select name="customerId" className="input-field" value={newInvoice.customerId} onChange={handleInputChange}>
            <option value="">Select</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <label className="input-label">Invoice Number</label>
          <input type="text" name="invoiceNumber" className="input-field" value={newInvoice.invoiceNumber} onChange={handleInputChange} />

          <label className="input-label">Date</label>
          <input type="date" name="date" className="input-field" value={newInvoice.date} onChange={handleInputChange} />

          <label className="input-label">Due Date</label>
          <input type="date" name="dueDate" className="input-field" value={newInvoice.dueDate} onChange={handleInputChange} />

          <label className="input-label">Status</label>
          <select name="status" className="input-field" value={newInvoice.status} onChange={handleInputChange}>
            <option>Draft</option>
            <option>Sent</option>
            <option>Paid</option>
            <option>Overdue</option>
          </select>

          <label className="input-label">Tax Rate (%)</label>
          <input type="number" name="taxRate" className="input-field" value={newInvoice.taxRate} onChange={handleInputChange} step="0.01" />

          <label><input type="checkbox" checked={newInvoice.isRecurring} onChange={e => setNewInvoice({...newInvoice, isRecurring: e.target.checked})} /> Recurring</label>
          {newInvoice.isRecurring && (
            <>
              <select name="recurrenceInterval" className="input-field" value={newInvoice.recurrenceInterval} onChange={handleInputChange}>
                <option>Weekly</option>
                <option>Monthly</option>
                <option>Quarterly</option>
              </select>
              <input type="date" name="recurrenceEndDate" className="input-field" value={newInvoice.recurrenceEndDate} onChange={handleInputChange} />
            </>
          )}

          <h4>Items</h4>
          {newInvoice.items.map((item, i) => (
            <>
              <div key={i} className="column">
                <label className="input-label">Description</label>
                <input type="text" className="input-field" placeholder="Description" value={item.description} onChange={e => handleItemChange(i, 'description', e.target.value)} />
                <div className="row gap-1 align-center justify-center">
                  <div className="column">
                    <label className="input-label">Quantity</label>
                    <input type="number" className="input-field" value={item.quantity} onChange={e => handleItemChange(i, 'quantity', e.target.value)} />
                  </div>
                  <div className="column">
                    <label className="input-label">Price</label>
                    <input type="number" className="input-field" value={item.unitPrice} onChange={e => handleItemChange(i, 'unitPrice', e.target.value)} step="0.01" />
                  </div>
                  <button type="button" className="remove-button" onClick={() => removeItem(i)}>
                    <i className="fas fa-trash font-red"></i>
                  </button>
                </div>                
              </div>
              <hr />
            </>
          ))}
          <button type="button" className="login-button" onClick={addItem}>Add Item</button>

          <p>Subtotal: {formatAmount(calculateSubtotal())}</p>
          <p>Tax: {formatAmount(calculateTax())}</p>
          <p>Total: {formatAmount(calculateTotal())}</p>

          <div className="row align-center justify-center gap-1">
            <button type="button" className="login-button" onClick={() => { setIsEditModalOpen(false); resetNewInvoice(); setEditingInvoice(null); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleUpdateInvoice}>Update Invoice</button>
          </div>
        </form>
      </Modal>

      {/* Template Modal */}
      <Modal isOpen={isTemplateModalOpen} onRequestClose={() => setIsTemplateModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <div className="modal-scroll">
          <h2>Templates</h2>
          {templates.length > 0 ? templates.map(t => (
            <div key={t.id} className="class-item">
              <p>{t.name} <br /> Items: {t.items.length} | Tax: {t.taxRate}% {t.isRecurring && '| Recurring'}</p>
              <button className="list-btn" onClick={() => applyTemplate(t)}>
                <i className="fas fa-check"></i>
              </button>
            </div>
          )) : <p>No templates yet. Save from Add Invoice form.</p>}
          <button className="login-button" onClick={() => setIsTemplateModalOpen(false)}>Close</button>
        </div>
      </Modal>
    </>
  );
};

export default Invoicing;